'use client';

import { faCalendarDays, faClinicMedical } from '@fortawesome/pro-light-svg-icons';
import { tv } from 'tailwind-variants';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';
import { Partner, PartnerScheduleSearchResult } from '@medprev-web-app/api-graphql-client';
import { v4 as uuidv4 } from 'uuid';

import { Card } from '@/components/ui/card.component';
import { Icon } from '@/components/ui/icon.component';
import { Button } from '@/components/ui/button/button.component';
import { Link } from '@/components/ui/link/link.component';
import { resolvePartnerName } from '@/utils/partner/partner.util';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';
import { useAgendamentoNavigation } from '@/hooks/navigation/use-agendamento-navigation.hook';

const branchLaboratoryAsideCardStyle = tv({
  slots: {
    card: 'w-full mt-4 xl:mt-0 rounded-xl px-10 py-8 sticky top-6',
  },
});

export interface PartnerAsideCardProps extends React.HTMLAttributes<HTMLDivElement> {
  partner?: Partner | null;
  inPersonSchedules?: PartnerScheduleSearchResult[];
  telehealthSchedules?: PartnerScheduleSearchResult[];
}

export function PartnerAsideCard({
  className,
  partner,
  inPersonSchedules,
  telehealthSchedules,
}: PartnerAsideCardProps) {
  const { card } = branchLaboratoryAsideCardStyle();
  const { buildLink } = useAgendamentoNavigation();

  const partnerSchedules
    = Number(inPersonSchedules?.length) > 0 ? inPersonSchedules?.slice(0, 5) : telehealthSchedules?.slice(0, 5);

  return (
    <Card className={card({ className })}>
      <div className="flex flex-col gap-6">
        <div className="flex items-start gap-2">
          <Icon icon={faClinicMedical} className="text-2xl mt-1 text-blue-600 w-6 h-6" />

          <div className="max-w-lg">
            <h3 className="text-xl font-semibold text-gray-800 break-words text-wrap hyphens-auto overflow-hidden">
              Agende um atendimento com
              {' '}
              {resolvePartnerName(partner as Partner)}
            </h3>
          </div>
        </div>

        <Button
          data-testid={DataTestIdEnum.PARTNER_PROFILE_SERVICES_BUTTON}
          startIcon={faCalendarDays}
          data-analytics-event={AnalyticsEnum.CLICK_PARTNER_REDIRECT_MODAL}
          asChild
        >
          <Link
            prefetch={false}
            href={buildLink({ path: `/parceiros/${partner?.id}/` })}
          >
            Serviços e horários
          </Link>
        </Button>

        {Number(partnerSchedules?.length) > 0 && (
          <div>
            <span className="text-gray-700">Veja alguns atendimentos oferecidos:</span>

            <ul className="mt-2 list-disc space-y-2">
              {partnerSchedules?.map((schedule) => (
                <li
                  data-testid={`partner-profile--procedure-${schedule.procedures[0].id}`}
                  key={`${schedule.procedures[0].id}-${uuidv4()}`}
                  className="text-gray-700 ml-6"
                >
                  {schedule.procedures[0].name}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Card>
  );
}
